export type RetryBreakdown = Record<number, number>;

export interface AttemptArm {
  successes: number;
  attempts: number;
}

export interface BreakdownSplit {
  first: number;
  retries: number;
}

export interface RetryIndexed {
  retry_index?: number | null;
}

export interface DayRetryBreakdowns {
  successful?: RetryBreakdown;
  preauth?: RetryBreakdown;
  failed?: RetryBreakdown;
}

export interface DayPaymentBuckets {
  successful?: readonly RetryIndexed[];
  preauth?: readonly RetryIndexed[];
  failed?: readonly RetryIndexed[];
}

export interface DayAttemptSplit {
  firstAttempt: AttemptArm;
  retries: AttemptArm;
}

/**
 * A dunning retry is stored as its own payment row sharing the billing cycle of the attempt it
 * follows, so a bucket total counts cycles more than once. `retry_index` is unbounded in practice
 * (23 observed in production), which is why every index above 0 collapses into a single figure
 * instead of being enumerated.
 */
export const splitBreakdown = (breakdown?: RetryBreakdown): BreakdownSplit => {
  let first = 0;
  let retries = 0;

  for (const [key, value] of Object.entries(breakdown ?? {})) {
    const retryIndex = Number(key);
    if (!Number.isInteger(retryIndex) || retryIndex < 0) continue;
    if (!Number.isFinite(value) || value <= 0) continue;

    if (retryIndex === 0) first += value;
    else retries += value;
  }

  return { first, retries };
};

/** Same split, for the day-detail payload: it carries raw payment rows instead of a breakdown. */
export const splitByRetryIndex = (payments?: readonly RetryIndexed[]): BreakdownSplit => {
  let first = 0;
  let retries = 0;

  for (const payment of payments ?? []) {
    const retryIndex = Number(payment?.retry_index ?? 0);
    if (!Number.isInteger(retryIndex) || retryIndex < 0) continue;

    if (retryIndex === 0) first += 1;
    else retries += 1;
  }

  return { first, retries };
};

const toArms = (successful: BreakdownSplit, preauth: BreakdownSplit, failed: BreakdownSplit): DayAttemptSplit => ({
  firstAttempt: {
    successes: successful.first,
    attempts: successful.first + preauth.first + failed.first,
  },
  retries: {
    successes: successful.retries,
    attempts: successful.retries + preauth.retries + failed.retries,
  },
});

export const splitDayAttempts = (breakdowns: DayRetryBreakdowns): DayAttemptSplit =>
  toArms(
    splitBreakdown(breakdowns.successful),
    splitBreakdown(breakdowns.preauth),
    splitBreakdown(breakdowns.failed)
  );

export const splitDayPayments = (buckets: DayPaymentBuckets): DayAttemptSplit =>
  toArms(
    splitByRetryIndex(buckets.successful),
    splitByRetryIndex(buckets.preauth),
    splitByRetryIndex(buckets.failed)
  );

export const approvalRate = (arm: AttemptArm): number | null =>
  arm.attempts > 0 ? (arm.successes / arm.attempts) * 100 : null;

export const formatArm = (arm: AttemptArm): string => {
  const rate = approvalRate(arm);
  const ratio = `${arm.successes}/${arm.attempts}`;

  return rate === null ? ratio : `${ratio} (${rate.toFixed(1)}%)`;
};
